import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import {
  PlusIcon, ArrowUpTrayIcon, TrashIcon,
  CalendarDaysIcon, ListBulletIcon, CheckIcon, ArrowUpCircleIcon,
  PhotoIcon, PlayCircleIcon, PaperClipIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon,
  ClipboardDocumentIcon, HashtagIcon, Squares2X2Icon, ClockIcon, ShieldCheckIcon, QrCodeIcon,
  CheckCircleIcon, XCircleIcon, ArrowTopRightOnSquareIcon, PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tip } from '../components/ui/tip';
import { DatePicker } from '../components/ui/date-picker';
import { confirmDialog } from '../components/ConfirmDialog';
import { promptDialog } from '../components/PromptDialog';

// ── Tipos ──────────────────────────────────────────────────────────────
interface Post {
  id: string;
  num: string;
  category: 'real' | 'edu' | 'marca';
  format: 'post' | 'carrusel' | 'reel' | 'historia';
  pista?: string;
  theme?: string;
  dayNumber?: number | null;
  isStoryFeatured?: number | boolean;
  realizado?: number | boolean;
  subido?: number | boolean;
  note?: string;
  tags?: string[];
  caption?: string;
  reviewStatus?: string; // '' borrador | 'revision' | 'aprobado'
  igLink?: string;
}
interface HashtagSet { id: string; name: string; tags: string[]; }
interface Ephemeride { type: 'patria' | 'rubro' | 'carrera'; dateLabel: string; name: string; day?: number; kind?: string; }
interface Calendar {
  id: string; name: string; month: number; year: number;
  account?: string; location?: string; distribution: any; ephemerides: any; posts: Post[];
}
interface GCalEvt { id: string; title: string; start: string; allDay?: boolean; color?: string | null; calendarName?: string; }

// Fecha local de un evento de Google (los all-day vienen como YYYY-MM-DD sin hora)
const gcalLocalDate = (e: GCalEvt): Date => {
  const s = String(e.start || '');
  if (e.allDay || /^\d{4}-\d{2}-\d{2}$/.test(s)) { const [y, mo, da] = s.slice(0, 10).split('-').map(Number); return new Date(y, (mo || 1) - 1, da || 1); }
  return new Date(s);
};

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DOW_SHORT = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const DAY_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const FMT_LABEL: Record<string, string> = { post: 'Post', carrusel: 'Carrusel', reel: 'Reel', historia: 'Historia' };

const CAT: Record<string, { label: string; dot: string; badge: string; border: string; bar: string }> = {
  real: { label: 'Trabajo real', dot: 'bg-blue-500', badge: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30', border: 'border-l-blue-500', bar: 'bg-blue-500' },
  edu: { label: 'Educación', dot: 'bg-violet-500', badge: 'bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30', border: 'border-l-violet-500', bar: 'bg-violet-500' },
  marca: { label: 'Marca', dot: 'bg-red-500', badge: 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30', border: 'border-l-red-500', bar: 'bg-red-500' },
};

const parseJson = (v: any, fb: any) => { if (v == null) return fb; if (typeof v === 'object') return v; try { return JSON.parse(v); } catch { return fb; } };

function buildGrid(month: number, year: number) {
  const first = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const startOffset = (first.getDay() + 6) % 7;
  const cells: Array<{ day: number; inMonth: boolean; dow: number }> = [];
  const prevDays = new Date(year, month - 1, 0).getDate();
  for (let i = startOffset - 1; i >= 0; i--) cells.push({ day: prevDays - i, inMonth: false, dow: 0 });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, inMonth: true, dow: ((new Date(year, month - 1, d).getDay()) + 6) % 7 });
  while (cells.length % 7 !== 0) { const out = cells.filter(c => !c.inMonth && c.day < 10); cells.push({ day: out.length + 1, inMonth: false, dow: 0 }); }
  const weeks: typeof cells[] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

// Tile del feed: muestra la primera foto del día (si hay) o un placeholder por categoría
const FeedTile: React.FC<{ post: Post; folderId?: string; onClick: () => void }> = ({ post, folderId, onClick }) => {
  const [thumb, setThumb] = useState<string | null>(null);
  useEffect(() => {
    if (!folderId) { setThumb(null); return; }
    let dead = false; let obj: string | null = null;
    axios.get('/api/google-drive/files', { params: { folderId } })
      .then(r => {
        const first = (r.data.files || [])[0];
        if (!first || dead) return;
        return axios.get(`/api/google-drive/files/${first.id}/thumbnail`, { responseType: 'blob' })
          .then(rr => { if (dead) return; obj = URL.createObjectURL(rr.data); setThumb(obj); });
      }).catch(() => {});
    return () => { dead = true; if (obj) URL.revokeObjectURL(obj); };
  }, [folderId]);
  const c = CAT[post.category];
  return (
    <button onClick={onClick} className="relative aspect-square overflow-hidden bg-muted group">
      {thumb ? (
        <img src={thumb} alt={post.theme} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center gap-1 bg-card">
          <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />
          <span className="text-[10px] leading-tight line-clamp-4 text-muted-foreground">{post.theme || 'Sin título'}</span>
        </div>
      )}
      <span className="absolute top-1 left-1 text-[9px] bg-black/60 text-white px-1 rounded">{post.dayNumber}</span>
      <span className="absolute top-1 right-1 text-[9px] bg-black/60 text-white px-1 rounded">{FMT_LABEL[post.format]}</span>
      {post.subido && <span className="absolute bottom-1 right-1 text-[9px] bg-blue-500 text-white px-1 rounded">↑</span>}
      <span className="absolute inset-0 group-hover:bg-black/10 transition-colors" />
    </button>
  );
};

const ContentCalendarPage: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const isAdmin = user?.role === 'admin' || hasPermission('manage_settings');
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [active, setActive] = useState<Calendar | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'calendar' | 'list' | 'feed'>(typeof window !== 'undefined' && window.innerWidth < 900 ? 'list' : 'calendar');

  const today = new Date();
  const todayDay = today.getDate(), todayMonth = today.getMonth() + 1, todayYear = today.getFullYear();

  // Cursor de mes navegado (infinito, como Google Calendar). NO crea calendarios.
  const [viewM, setViewM] = useState(todayMonth);
  const [viewY, setViewY] = useState(todayYear);

  const [modalPost, setModalPost] = useState<Post | null>(null);
  const [editMode, setEditMode] = useState(false);
  useEffect(() => { setEditMode(false); }, [modalPost?.id]);
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showDateConfig, setShowDateConfig] = useState(false);
  const [dayFolders, setDayFolders] = useState<Record<number, string>>({});

  const fetchList = useCallback(async () => {
    try {
      const res = await axios.get('/api/content-calendars');
      const list: Calendar[] = res.data.calendars || [];
      setCalendars(list);
      return list;
    } catch { toast.error('Error al cargar calendarios'); return []; }
    finally { setLoading(false); }
  }, []);

  const fetchDayFolders = useCallback(async (id: string) => {
    try { const res = await axios.get(`/api/content-calendars/${id}/day-folders`); setDayFolders(res.data.dayFolders || {}); }
    catch { setDayFolders({}); }
  }, []);

  // Recargar el calendario activo (el del mes navegado) desde la lista
  const reloadActive = useCallback(async () => {
    const cal = calendars.find(c => c.month === viewM && c.year === viewY);
    if (cal) {
      try { const res = await axios.get(`/api/content-calendars/${cal.id}`); setActive(res.data); } catch {}
      fetchDayFolders(cal.id);
    } else {
      setActive(null); setDayFolders({});
    }
  }, [calendars, viewM, viewY, fetchDayFolders]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { reloadActive(); }, [reloadActive]);

  // Calendario real del mes visto (puede no existir todavía)
  const activeCal = useMemo(() => calendars.find(c => c.month === viewM && c.year === viewY) || null, [calendars, viewM, viewY]);
  const hasCalendar = !!active && active.month === viewM && active.year === viewY;

  const dist = useMemo(() => parseJson(active?.distribution, { real: 70, edu: 20, marca: 10 }), [active]);
  const ephemerides: Ephemeride[] = useMemo(() => hasCalendar ? parseJson(active?.ephemerides, []) : [], [active, hasCalendar]);
  const posts = hasCalendar ? (active?.posts || []) : [];

  const postsByDay = useMemo(() => { const m: Record<number, Post[]> = {}; posts.forEach(p => { if (p.dayNumber != null) (m[Number(p.dayNumber)] = m[Number(p.dayNumber)] || []).push(p); }); return m; }, [posts]);
  const efemByDay = useMemo(() => { const m: Record<number, Ephemeride[]> = {}; ephemerides.forEach(e => { if (e.day != null) (m[e.day] = m[e.day] || []).push(e); }); return m; }, [ephemerides]);

  // Eventos del mes: Google (feriados sin islámicas/judías, sin turnos) + fechas curadas ROCAM
  const [gcalEvents, setGcalEvents] = useState<GCalEvt[]>([]);
  const [cfgReload, setCfgReload] = useState(0);
  const CAT_COLOR: Record<string, string> = { comercial: '#a855f7', rubro: '#f59e0b', tc: '#ef4444', mundial: '#10b981' };
  useEffect(() => {
    let dead = false;
    const timeMin = new Date(viewY, viewM - 1, 1).toISOString();
    const timeMax = new Date(viewY, viewM, 0, 23, 59, 59).toISOString();
    Promise.all([
      axios.get('/api/google-calendar/events', { params: { timeMin, timeMax, filterReligious: 1 } })
        .then(r => r.data.events || []).catch(() => []),
      axios.get('/api/content-calendars/special-dates', { params: { year: viewY, month: viewM } })
        .then(r => (r.data.dates || []).map((d: any) => ({
          id: 'cur-' + d.id,
          title: `${d.emoji || ''} ${d.title}`.trim(),
          start: d.date,
          allDay: true,
          color: CAT_COLOR[d.category] || '#6366f1',
          calendarName: 'ROCAM',
        }))).catch(() => []),
    ]).then(([g, c]) => { if (!dead) setGcalEvents([...g, ...c]); });
    return () => { dead = true; };
  }, [viewM, viewY, cfgReload]);

  const gcalByDay = useMemo(() => {
    const m: Record<number, GCalEvt[]> = {};
    gcalEvents.forEach(e => { const d = gcalLocalDate(e); if (d.getMonth() + 1 === viewM && d.getFullYear() === viewY) (m[d.getDate()] = m[d.getDate()] || []).push(e); });
    return m;
  }, [gcalEvents, viewM, viewY]);

  const gcalSorted = useMemo(() => Object.keys(gcalByDay).map(Number).sort((a, b) => a - b).flatMap(d => gcalByDay[d].map(e => ({ day: d, e }))), [gcalByDay]);

  const counts = useMemo(() => {
    const total = posts.length;
    const stories = posts.filter(p => p.format === 'historia');
    const h = stories.length;                       // historias planificadas
    const hDone = stories.filter(p => p.realizado).length; // historias hechas
    const feed = total - h;                          // posteos del feed (sin historias)
    const r = posts.filter(p => p.realizado).length;
    const s = posts.filter(p => p.subido).length;
    const byCat = { real: 0, edu: 0, marca: 0 } as Record<string, number>;
    posts.forEach(p => byCat[p.category] = (byCat[p.category] || 0) + 1);
    // Distribución real en % (cuenta TODO: posteos + historias)
    const realPct = { real: 0, edu: 0, marca: 0 } as Record<string, number>;
    if (total > 0) (['real', 'edu', 'marca'] as const).forEach(c => realPct[c] = Math.round(byCat[c] / total * 100));
    return { total, feed, h, hDone, r, s, byCat, realPct };
  }, [posts]);

  // Crea el calendario real del mes navegado SOLO cuando se necesita (al cargar contenido).
  const ensureCalendar = async (): Promise<string> => {
    const existing = calendars.find(c => c.month === viewM && c.year === viewY);
    if (existing) return existing.id;
    const res = await axios.post('/api/content-calendars', { name: `${MONTHS[viewM - 1]} ${viewY}`, month: viewM, year: viewY, distribution: { real: 70, edu: 20, marca: 10 }, ephemerides: [], posts: [] });
    await fetchList();
    return res.data.id;
  };
  const goToPeriod = (m: number, y: number) => { setViewM(m); setViewY(y); };

  // Editor del objetivo de distribución
  const [goalOpen, setGoalOpen] = useState(false);
  const [goalForm, setGoalForm] = useState({ real: 70, edu: 20, marca: 10 });
  const openGoal = () => { setGoalForm({ real: Number(dist.real) || 0, edu: Number(dist.edu) || 0, marca: Number(dist.marca) || 0 }); setGoalOpen(true); };
  const saveGoal = async () => {
    const sum = goalForm.real + goalForm.edu + goalForm.marca;
    if (sum !== 100) { toast.error(`Los porcentajes deben sumar 100 (ahora suman ${sum})`); return; }
    setGoalOpen(false);
    try {
      const calId = await ensureCalendar();
      await axios.put(`/api/content-calendars/${calId}`, { distribution: goalForm });
      toast.success('Objetivo actualizado'); reloadActive();
    } catch { toast.error('Error al guardar'); reloadActive(); }
  };

  const weeks = useMemo(() => buildGrid(viewM, viewY), [viewM, viewY]);
  const isCurrentMonth = viewM === todayMonth && viewY === todayYear;

  const dowOf = (day: number) => ((new Date(viewY, viewM - 1, day).getDay()) + 6) % 7;

  const togglePost = async (post: Post, key: 'realizado' | 'subido') => {
    const next = { realizado: !!post.realizado, subido: !!post.subido };
    if (key === 'realizado') { next.realizado = !next.realizado; if (!next.realizado) next.subido = false; }
    else { next.subido = !next.subido; if (next.subido) next.realizado = true; }
    setActive(prev => prev ? { ...prev, posts: prev.posts.map(p => p.id === post.id ? { ...p, ...next } : p) } : prev);
    if (modalPost?.id === post.id) setModalPost(p => p ? { ...p, ...next } : p);
    try { await axios.put(`/api/content-calendars/posts/${post.id}`, next); } catch { toast.error('Error al guardar'); reloadActive(); }
  };
  const saveNote = async (post: Post, note: string) => {
    setActive(prev => prev ? { ...prev, posts: prev.posts.map(p => p.id === post.id ? { ...p, note } : p) } : prev);
    try { await axios.put(`/api/content-calendars/posts/${post.id}`, { note }); } catch {}
  };
  const savePost = async (post: Post, patch: Partial<Post>) => {
    setActive(prev => prev ? { ...prev, posts: prev.posts.map(p => p.id === post.id ? { ...p, ...patch } : p) } : prev);
    if (modalPost?.id === post.id) setModalPost(p => p ? { ...p, ...patch } : p);
    try { await axios.put(`/api/content-calendars/posts/${post.id}`, patch); } catch { toast.error('Error al guardar'); }
  };
  const [tagInput, setTagInput] = useState('');
  useEffect(() => { setTagInput(''); }, [modalPost?.id]);
  const addTag = (post: Post) => {
    const t = tagInput.trim().replace(/^#/, '');
    if (!t) return;
    const cur = post.tags || [];
    if (cur.some(x => x.toLowerCase() === t.toLowerCase())) { setTagInput(''); return; }
    savePost(post, { tags: [...cur, t] }); setTagInput('');
  };
  const removeTag = (post: Post, t: string) => savePost(post, { tags: (post.tags || []).filter(x => x !== t) });

  // Copiar el texto del posteo + hashtags para pegar en Instagram
  const copyCaption = (post: Post) => {
    const tags = (post.tags || []).map(t => `#${t}`).join(' ');
    const txt = [post.caption || '', tags].filter(Boolean).join('\n\n');
    if (!txt.trim()) { toast.error('No hay texto ni hashtags'); return; }
    navigator.clipboard.writeText(txt).then(() => toast.success('Copiado para Instagram')).catch(() => toast.error('No se pudo copiar'));
  };

  // Sets de hashtags
  const [hashtagSets, setHashtagSets] = useState<HashtagSet[]>([]);
  const [setsOpen, setSetsOpen] = useState(false);
  const fetchSets = useCallback(async () => {
    try { const r = await axios.get('/api/content-calendars/hashtag-sets'); setHashtagSets(r.data.sets || []); } catch {}
  }, []);
  useEffect(() => { fetchSets(); }, [fetchSets]);
  const insertSet = (post: Post, set: HashtagSet) => {
    const cur = post.tags || [];
    const merged = [...cur];
    set.tags.forEach(t => { if (!merged.some(x => x.toLowerCase() === t.toLowerCase())) merged.push(t); });
    savePost(post, { tags: merged });
    toast.success(`Set "${set.name}" agregado`);
  };
  const saveCurrentAsSet = async (post: Post) => {
    if (!(post.tags || []).length) { toast.error('La pieza no tiene etiquetas para guardar'); return; }
    const name = await promptDialog({ title: 'Guardar set de hashtags', placeholder: 'Nombre del set', confirmText: 'Guardar' });
    if (!name || !name.trim()) return;
    try { await axios.post('/api/content-calendars/hashtag-sets', { name: name.trim(), tags: post.tags }); toast.success('Set guardado'); fetchSets(); }
    catch (e: any) { toast.error(e.response?.data?.error || 'Error al guardar'); }
  };
  const deleteSet = async (id: string) => {
    try { await axios.delete(`/api/content-calendars/hashtag-sets/${id}`); fetchSets(); } catch { toast.error('Error'); }
  };

  const createPiece = async (day: number) => {
    try {
      const calId = await ensureCalendar();
      const num = String((posts.length || 0) + 1).padStart(2, '0');
      const r = await axios.post(`/api/content-calendars/${calId}/posts`, { num, category: 'real', format: 'post', pista: '', theme: '', dayNumber: day });
      const res = await axios.get(`/api/content-calendars/${calId}`);
      setActive(res.data);
      const created = (res.data.posts || []).find((p: Post) => p.id === r.data.id);
      if (created) setModalPost(created);
      toast.success('Pieza creada');
    } catch { toast.error('No se pudo crear la pieza'); }
  };
  const deletePiece = async (post: Post) => {
    if (!(await confirmDialog({ title: 'Eliminar pieza', description: '¿Eliminar este posteo del calendario?', confirmText: 'Eliminar' }))) return;
    try {
      await axios.delete(`/api/content-calendars/posts/${post.id}`);
      setActive(prev => prev ? { ...prev, posts: prev.posts.filter(p => p.id !== post.id) } : prev);
      setModalPost(null); toast.success('Pieza eliminada');
    } catch (e: any) {
      // 404 = la pieza ya no existe (borrada en otra sesión/pestaña). Reconciliar la vista en vez de mostrar error.
      if (e?.response?.status === 404) {
        setActive(prev => prev ? { ...prev, posts: prev.posts.filter(p => p.id !== post.id) } : prev);
        setModalPost(null); toast.success('La pieza ya estaba eliminada'); reloadActive();
      } else { toast.error('Error al eliminar'); }
    }
  };

  useEffect(() => { const h = (e: KeyboardEvent) => { if (e.key === 'Escape') setModalPost(null); }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, []);

  // Navegar mes: solo mueve el cursor (NO crea nada, ni en el sistema ni en Drive)
  const gotoMonth = (delta: number) => {
    let m = viewM + delta, y = viewY;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setViewM(m); setViewY(y);
  };
  const sortedPosts = useMemo(() => [...posts].sort((a, b) => (Number(a.dayNumber) || 99) - (Number(b.dayNumber) || 99)), [posts]);
  // Feed: más nuevo primero (como Instagram)
  const feedPosts = useMemo(() => [...posts].sort((a, b) => (Number(b.dayNumber) || 0) - (Number(a.dayNumber) || 0)), [posts]);

  const setReview = (post: Post, status: string) => savePost(post, { reviewStatus: status });
  const reviewBadge = (post: Post) => {
    if (post.reviewStatus === 'revision') return <span className="text-[9px] px-1 rounded bg-amber-500/20 text-amber-600 dark:text-amber-400 font-medium">Revisión</span>;
    if (post.reviewStatus === 'aprobado') return <span className="text-[9px] px-1 rounded bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 font-medium">Aprobado</span>;
    return null;
  };

  // Mini card de pieza para apilar varias por día en la grilla
  const PostMini = (post: Post) => {
    const c = CAT[post.category];
    return (
      <button key={post.id} onClick={() => setModalPost(post)}
        className={`w-full text-left rounded-md border border-l-4 ${c.border} bg-card hover:bg-muted/40 p-1.5 transition-colors ${post.subido ? 'opacity-80' : ''}`}>
        <div className="flex items-center gap-1 mb-0.5">
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${c.dot}`} />
          <span className="text-[9px] text-muted-foreground uppercase tracking-wide truncate">#{post.num}{post.pista ? ` · ${post.pista}` : ''}</span>
          <Badge variant="secondary" className="text-[9px] ml-auto shrink-0 px-1 py-0">{FMT_LABEL[post.format]}</Badge>
        </div>
        <div className="text-[11px] leading-snug line-clamp-2">{post.theme || <span className="text-muted-foreground italic">Sin título</span>}</div>
        <div className="flex items-center gap-1 mt-1">
          {reviewBadge(post)}
          <Tip label="Realizado"><span onClick={(e) => { e.stopPropagation(); togglePost(post, 'realizado'); }}
            className={`ml-auto text-center text-[10px] px-1.5 py-0.5 rounded border cursor-pointer ${post.realizado ? 'bg-muted border-border font-medium' : 'border-border/50 text-muted-foreground'}`}>✓</span></Tip>
          <Tip label="Subido"><span onClick={(e) => { e.stopPropagation(); togglePost(post, 'subido'); }}
            className={`text-center text-[10px] px-1.5 py-0.5 rounded border cursor-pointer ${post.subido ? 'bg-blue-500 text-white border-blue-500 font-medium' : 'border-border/50 text-muted-foreground'}`}>↑</span></Tip>
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Calendario de contenido</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Planificación de contenido para Instagram</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-1">
            <Tip label="Mes anterior"><Button variant="outline" size="icon" className="h-9 w-9 rounded-lg shrink-0" onClick={() => gotoMonth(-1)}><ChevronLeftIcon className="h-4 w-4" /></Button></Tip>
            <span className="text-sm font-semibold min-w-[150px] text-center select-none">{MONTHS[viewM - 1]} {viewY}</span>
            <Tip label="Mes siguiente"><Button variant="outline" size="icon" className="h-9 w-9 rounded-lg shrink-0" onClick={() => gotoMonth(1)}><ChevronRightIcon className="h-4 w-4" /></Button></Tip>
          </div>
          <Button variant="outline" size="sm" className="h-9 rounded-lg" onClick={() => goToPeriod(todayMonth, todayYear)}>Hoy</Button>
          <Button variant="outline" size="sm" className="h-9 rounded-lg gap-1.5" onClick={() => setShowImport(true)}>
            <ArrowUpTrayIcon className="h-4 w-4" /> Importar
          </Button>
          <Tip label="Configurar fechas y categorías del calendario"><Button variant="outline" size="sm" className="h-9 rounded-lg gap-1.5" onClick={() => setShowDateConfig(true)}>
            <CalendarDaysIcon className="h-4 w-4" /> Fechas
          </Button></Tip>
        </div>
      </div>

      <DateConfigDialog open={showDateConfig} onClose={() => setShowDateConfig(false)} onChanged={() => setCfgReload((x) => x + 1)} />

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>
      ) : (
        <>
          {/* Resumen + acciones del calendario activo */}
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 flex-1">
              <Card><CardContent className="pt-4 pb-3">
                <p className="text-xs text-muted-foreground">Período</p>
                <p className="text-lg font-bold mt-0.5">{MONTHS[viewM - 1]} {viewY}</p>
              </CardContent></Card>
              <Card><CardContent className="pt-4 pb-3">
                <p className="text-xs text-muted-foreground">Posteos</p>
                <p className="text-lg font-bold mt-0.5">{counts.feed}</p>
              </CardContent></Card>
              <Card><CardContent className="pt-4 pb-3">
                <p className="text-xs text-muted-foreground">Historias</p>
                <p className="text-lg font-bold mt-0.5 text-fuchsia-600 dark:text-fuchsia-400">{counts.hDone}<span className="text-sm text-muted-foreground">/{counts.h}</span></p>
              </CardContent></Card>
              <Card><CardContent className="pt-4 pb-3">
                <p className="text-xs text-muted-foreground">Realizados</p>
                <p className="text-lg font-bold mt-0.5">{counts.r}<span className="text-sm text-muted-foreground">/{counts.total}</span></p>
              </CardContent></Card>
              <Card><CardContent className="pt-4 pb-3">
                <p className="text-xs text-muted-foreground">Subidos</p>
                <p className="text-lg font-bold mt-0.5 text-blue-600 dark:text-blue-400">{counts.s}<span className="text-sm text-muted-foreground">/{counts.total}</span></p>
              </CardContent></Card>
            </div>
          </div>

          {/* Barras de reparto: objetivo (editable) + cómo venimos */}
          <Card><CardContent className="py-4 space-y-4">
            {/* Objetivo */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Objetivo del mes</span>
                <div className="flex items-center gap-3">
                  <div className="hidden sm:flex gap-4 text-xs text-muted-foreground">
                    {(['real', 'edu', 'marca'] as const).map(c => (
                      <span key={c} className="flex items-center gap-1.5"><span className={`inline-block w-2.5 h-2.5 rounded-sm ${CAT[c].dot}`} /> {CAT[c].label}</span>
                    ))}
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={openGoal}>Editar</Button>
                </div>
              </div>
              <div className="flex h-7 rounded-md overflow-hidden">
                {(['real', 'edu', 'marca'] as const).map(c => (
                  <div key={c} className={`${CAT[c].bar} flex items-center justify-center text-xs font-semibold text-white`} style={{ flex: Number(dist[c]) || 0 }}>{dist[c]}%</div>
                ))}
              </div>
            </div>

            {/* Cómo venimos (real) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Cómo venimos <span className="text-xs text-muted-foreground font-normal">({counts.total} piezas)</span></span>
                <div className="flex gap-4 text-xs text-muted-foreground">
                  {(['real', 'edu', 'marca'] as const).map(c => {
                    const off = counts.total > 0 && Math.abs(counts.realPct[c] - (Number(dist[c]) || 0)) >= 10;
                    return <span key={c} className="flex items-center gap-1.5"><span className={`inline-block w-2.5 h-2.5 rounded-sm ${CAT[c].dot}`} /> {counts.byCat[c]} · <span className={off ? 'text-amber-500 font-medium' : ''}>{counts.realPct[c]}%</span></span>;
                  })}
                </div>
              </div>
              {counts.total > 0 ? (
                <div className="flex h-7 rounded-md overflow-hidden">
                  {(['real', 'edu', 'marca'] as const).map(c => counts.realPct[c] > 0 && (
                    <div key={c} className={`${CAT[c].bar} flex items-center justify-center text-xs font-semibold text-white`} style={{ flex: counts.realPct[c] }}>{counts.realPct[c]}%</div>
                  ))}
                </div>
              ) : (
                <div className="h-7 rounded-md border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">Sin piezas cargadas todavía</div>
              )}
            </div>
          </CardContent></Card>

          {/* Fechas del mes — desde Google Calendar (con fallback a las manuales) */}
          {gcalSorted.length > 0 ? (
            <Card><CardContent className="py-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
                <CalendarDaysIcon className="h-4 w-4" /> Fechas del mes · Google Calendar
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                {gcalSorted.map(({ day, e }) => (
                  <div key={`${e.id}-${day}`} className="flex gap-2 text-sm items-center min-w-0">
                    <span className="font-mono text-xs text-muted-foreground shrink-0 min-w-[60px]">{DOW_SHORT[dowOf(day)]} {String(day).padStart(2, '0')}</span>
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: e.color || '#f59e0b' }} />
                    <Tip label={e.calendarName ? `${e.title} · ${e.calendarName}` : e.title}><span className="truncate">{e.title}</span></Tip>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          ) : ephemerides.length > 0 ? (
            <Card><CardContent className="py-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(['patria', 'rubro', 'carrera'] as const).map(type => {
                  const items = ephemerides.filter(e => e.type === type);
                  const title = type === 'patria' ? 'Fechas patrias' : type === 'rubro' ? 'Rubro · estacional' : 'Carreras';
                  return (
                    <div key={type}>
                      <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">{title}</div>
                      <div className="space-y-1">
                        {items.length === 0 && <div className="text-xs text-muted-foreground/60">—</div>}
                        {items.map((e, i) => (
                          <div key={i} className="flex gap-2 text-sm">
                            <span className="font-mono text-xs text-muted-foreground shrink-0 min-w-[52px]">{e.dateLabel}</span>
                            <span dangerouslySetInnerHTML={{ __html: e.name }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent></Card>
          ) : null}

          {/* Toggle de vista */}
          <div className="flex items-center justify-between">
            <div className="inline-flex rounded-lg border border-border overflow-hidden">
              <button onClick={() => setView('calendar')} className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 transition-colors ${view === 'calendar' ? 'bg-foreground text-background' : 'bg-background hover:bg-muted'}`}>
                <CalendarDaysIcon className="h-4 w-4" /> Calendario
              </button>
              <button onClick={() => setView('list')} className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 border-l border-border transition-colors ${view === 'list' ? 'bg-foreground text-background' : 'bg-background hover:bg-muted'}`}>
                <ListBulletIcon className="h-4 w-4" /> Lista
              </button>
              <button onClick={() => setView('feed')} className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 border-l border-border transition-colors ${view === 'feed' ? 'bg-foreground text-background' : 'bg-background hover:bg-muted'}`}>
                <Squares2X2Icon className="h-4 w-4" /> Feed
              </button>
            </div>
          </div>

          {/* ── VISTA CALENDARIO ── */}
          {view === 'calendar' && (
            <Card><CardContent className="p-3 overflow-x-auto">
              <div className="min-w-[860px]">
                {/* cabecera días */}
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {DOW_SHORT.map((d, i) => <div key={d} className={`text-xs font-semibold uppercase tracking-wide px-2 ${i >= 5 ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}>{d}</div>)}
                </div>
                <div className="space-y-2">
                  {weeks.map((week, wi) => (
                    <div key={wi} className="grid grid-cols-7 gap-2">
                      {week.map((cell, ci) => {
                        if (!cell.inMonth) return <div key={ci} className="rounded-lg border border-dashed border-border/40 min-h-[120px] p-2 opacity-40"><span className="text-xs text-muted-foreground">{cell.day}</span></div>;
                        const isPast = !!isCurrentMonth && cell.day < todayDay;
                        const isToday = !!isCurrentMonth && cell.day === todayDay;
                        const dayPosts = postsByDay[cell.day] || [];
                        const efs = efemByDay[cell.day] || [];
                        const gcs = gcalByDay[cell.day] || [];
                        return (
                          <div key={ci} className={`group rounded-lg border min-h-[120px] p-2 flex flex-col ${dayPosts.length ? 'border-border' : isPast ? 'bg-muted/20 border-dashed' : 'border-border'} ${isToday ? 'ring-2 ring-blue-500/50' : ''}`}>
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className={`text-base font-bold ${isPast && !dayPosts.length ? 'text-muted-foreground/50' : ''}`}>{cell.day}</span>
                              {isToday && <span className="text-[10px] font-bold bg-blue-500 text-white px-1.5 py-0.5 rounded">Hoy</span>}
                              {dayFolders[cell.day] && <PaperClipIcon className="h-3.5 w-3.5 text-muted-foreground" />}
                              <Tip label="Agregar pieza"><button onClick={() => createPiece(cell.day)} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"><PlusIcon className="h-4 w-4" /></button></Tip>
                            </div>
                            {/* Fechas de Google / efemérides del día */}
                            {gcs.length > 0 ? (
                              <div className="space-y-1 mb-1">{gcs.slice(0, 2).map(e => (
                                <Tip key={e.id} label={e.calendarName ? `${e.title} · ${e.calendarName}` : e.title}>
                                  <div className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-muted/60 truncate">
                                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: e.color || '#f59e0b' }} /><span className="truncate">{e.title}</span>
                                  </div>
                                </Tip>
                              ))}</div>
                            ) : (!dayPosts.length && efs.length > 0) ? (
                              <div className="space-y-1 mb-1">{efs.map((e, i) => <div key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 truncate">{e.name.replace(/<[^>]+>/g, '')}</div>)}</div>
                            ) : null}
                            {/* Piezas del día */}
                            {dayPosts.length === 0 ? (
                              <div className="text-[10px] text-muted-foreground/60 mt-auto pt-1">Solo historias</div>
                            ) : (
                              <div className="space-y-1.5">{dayPosts.map(p => PostMini(p))}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent></Card>
          )}

          {/* ── VISTA LISTA ── */}
          {view === 'list' && (
            <Card><CardContent className="p-0">
              <div className="relative w-full overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-muted/50 border-b">
                      <TableHead className="h-10 px-3 font-medium w-16">Día</TableHead>
                      <TableHead className="h-10 px-3 font-medium w-14">#</TableHead>
                      <TableHead className="h-10 px-3 font-medium">Tema</TableHead>
                      <TableHead className="h-10 px-3 font-medium w-28 hidden md:table-cell">Categoría</TableHead>
                      <TableHead className="h-10 px-3 font-medium w-24 hidden md:table-cell">Formato</TableHead>
                      <TableHead className="h-10 px-3 font-medium w-44 text-center">Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedPosts.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Sin piezas cargadas</TableCell></TableRow>}
                    {sortedPosts.map(post => {
                      const c = CAT[post.category];
                      const day = Number(post.dayNumber);
                      const isToday = !!isCurrentMonth && day === todayDay;
                      return (
                        <TableRow key={post.id} className="border-b hover:bg-muted/50 cursor-pointer" onClick={() => setModalPost(post)}>
                          <TableCell className="px-3 py-2.5 align-middle whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold">{String(day).padStart(2, '0')}</span>
                              <span className="text-xs text-muted-foreground">{DOW_SHORT[dowOf(day)]}</span>
                              {isToday && <span className="text-[10px] font-bold bg-blue-500 text-white px-1.5 rounded">Hoy</span>}
                            </div>
                          </TableCell>
                          <TableCell className="px-3 py-2.5 align-middle font-mono text-sm text-muted-foreground">#{post.num}</TableCell>
                          <TableCell className="px-3 py-2.5 align-middle">
                            <div className="flex items-center gap-2">
                              <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${c.dot}`} />
                              <div className="min-w-0">
                                <div className="font-medium truncate flex items-center gap-1">{post.theme}{dayFolders[day] && <PaperClipIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}</div>
                                <div className="text-xs text-muted-foreground md:hidden">{post.pista} · {FMT_LABEL[post.format]}</div>
                                {(post.tags || []).length > 0 && <div className="text-[11px] text-muted-foreground truncate">{(post.tags || []).map(t => `#${t}`).join(' ')}</div>}
                                {post.note && <div className="text-xs text-blue-600 dark:text-blue-400 truncate">✎ {post.note}</div>}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="px-3 py-2.5 align-middle hidden md:table-cell">
                            <Badge variant="secondary" className={`text-xs ${c.badge}`}>{c.label}</Badge>
                          </TableCell>
                          <TableCell className="px-3 py-2.5 align-middle hidden md:table-cell">
                            <span className="text-sm text-muted-foreground">{FMT_LABEL[post.format]}</span>
                          </TableCell>
                          <TableCell className="px-3 py-2.5 align-middle" onClick={e => e.stopPropagation()}>
                            <div className="flex gap-1.5 justify-center">
                              <button onClick={() => togglePost(post, 'realizado')}
                                className={`text-xs px-2 py-1 rounded border flex items-center gap-1 ${post.realizado ? 'bg-muted border-border font-medium' : 'border-border/50 text-muted-foreground hover:bg-muted'}`}>
                                <CheckIcon className="h-3 w-3" /> Hecho
                              </button>
                              <button onClick={() => togglePost(post, 'subido')}
                                className={`text-xs px-2 py-1 rounded border flex items-center gap-1 ${post.subido ? 'bg-blue-500 text-white border-blue-500 font-medium' : 'border-border/50 text-muted-foreground hover:bg-muted'}`}>
                                <ArrowUpCircleIcon className="h-3 w-3" /> Subido
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent></Card>
          )}

          {/* ── VISTA FEED (preview de Instagram) ── */}
          {view === 'feed' && (
            <Card><CardContent className="p-4">
              {feedPosts.length === 0 ? (
                <div className="py-16 text-center text-sm text-muted-foreground">Todavía no hay piezas para previsualizar el feed.</div>
              ) : (
                <div className="max-w-sm mx-auto">
                  <p className="text-xs text-muted-foreground mb-3 text-center">Así se vería el perfil — más nuevo arriba. Las que tienen foto cargada se ven; el resto, con el tema.</p>
                  <div className="grid grid-cols-3 gap-0.5">
                    {feedPosts.map(p => <FeedTile key={p.id} post={p} folderId={dayFolders[Number(p.dayNumber)]} onClick={() => setModalPost(p)} />)}
                  </div>
                </div>
              )}
            </CardContent></Card>
          )}
        </>
      )}

      {/* EDITAR OBJETIVO */}
      <Dialog open={goalOpen} onOpenChange={o => { if (!o) setGoalOpen(false); }}>
        <DialogContent className="w-[95vw] max-w-sm">
          <DialogHeader><DialogTitle>Objetivo del mes</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Definí qué porcentaje querés de cada tipo. Tienen que sumar 100%.</p>
            {(['real', 'edu', 'marca'] as const).map(c => (
              <div key={c} className="flex items-center gap-3">
                <span className={`inline-block w-3 h-3 rounded-sm ${CAT[c].dot}`} />
                <span className="text-sm flex-1">{CAT[c].label}</span>
                <Input type="number" min={0} max={100} value={goalForm[c]} onChange={e => setGoalForm(f => ({ ...f, [c]: Math.max(0, Math.min(100, Number(e.target.value) || 0)) }))} className="w-20" />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
            ))}
            <p className={`text-xs ${goalForm.real + goalForm.edu + goalForm.marca === 100 ? 'text-muted-foreground' : 'text-amber-500 font-medium'}`}>
              Suma: {goalForm.real + goalForm.edu + goalForm.marca}%
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setGoalOpen(false)}>Cancelar</Button>
              <Button onClick={saveGoal}>Guardar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* SETS DE HASHTAGS */}
      <Dialog open={setsOpen} onOpenChange={o => { if (!o) setSetsOpen(false); }}>
        <DialogContent className="w-[95vw] max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><HashtagIcon className="h-5 w-5" /> Sets de hashtags</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Conjuntos de hashtags reutilizables. Tocá "Agregar" para sumarlos a esta pieza.</p>
            {hashtagSets.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">Todavía no hay sets guardados.</p>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                {hashtagSets.map(s => (
                  <div key={s.id} className="rounded-lg border border-border p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate">{s.name}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        {modalPost && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => insertSet(modalPost, s)}>Agregar</Button>}
                        <Tip label="Eliminar set"><button onClick={() => deleteSet(s.id)} className="text-muted-foreground hover:text-red-600 p-1"><TrashIcon className="h-4 w-4" /></button></Tip>
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1 break-words">{s.tags.map(t => `#${t}`).join(' ')}</p>
                  </div>
                ))}
              </div>
            )}
            {modalPost && (
              <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => saveCurrentAsSet(modalPost)}>
                <PlusIcon className="h-4 w-4" /> Guardar las etiquetas de esta pieza como set
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* MODAL PIEZA */}
      <Dialog open={!!modalPost} onOpenChange={o => { if (!o) setModalPost(null); }}>
        <DialogContent className="max-w-[1100px] w-[96vw] max-h-[92vh] overflow-y-auto">
          {modalPost && active && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${CAT[modalPost.category].dot}`} />
                  {CAT[modalPost.category].label} · #{modalPost.num}
                  <Button variant={editMode ? 'secondary' : 'outline'} size="sm" className="ml-auto mr-6 h-7 gap-1.5" onClick={() => setEditMode(v => !v)}>
                    {editMode ? <><CheckIcon className="h-3.5 w-3.5" /> Listo</> : <><PencilSquareIcon className="h-3.5 w-3.5" /> Editar</>}
                  </Button>
                </DialogTitle>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-x-6 gap-y-4 items-start">
                <div className="space-y-4">
                <div className="text-sm text-muted-foreground md:col-span-2">
                  {modalPost.dayNumber != null ? `${DAY_NAMES[dowOf(Number(modalPost.dayNumber))]} ${modalPost.dayNumber} de ${MONTHS[viewM - 1]}` : ''}
                </div>
                <div className="space-y-1.5">
                  <Label>Título</Label>
                  {editMode
                    ? <Input key={`t-${modalPost.id}`} defaultValue={modalPost.theme || ''} placeholder="Título del posteo"
                        onBlur={e => { if (e.target.value !== (modalPost.theme || '')) savePost(modalPost, { theme: e.target.value }); }} />
                    : <div className="text-sm px-3 py-2 rounded-md bg-muted/40 border border-border min-h-[38px]">{modalPost.theme || <span className="text-muted-foreground">Sin título</span>}</div>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label>Categoría</Label>
                    {editMode
                      ? <Select value={modalPost.category} onValueChange={v => savePost(modalPost, { category: v as Post['category'] })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="real">Trabajo real</SelectItem>
                            <SelectItem value="edu">Educación</SelectItem>
                            <SelectItem value="marca">Marca</SelectItem>
                          </SelectContent>
                        </Select>
                      : <div className="text-sm px-3 py-2 rounded-md bg-muted/40 border border-border min-h-[38px]">{CAT[modalPost.category].label}</div>}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Formato</Label>
                    {editMode
                      ? <Select value={modalPost.format} onValueChange={v => savePost(modalPost, { format: v as Post['format'] })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="post">Post</SelectItem>
                            <SelectItem value="carrusel">Carrusel</SelectItem>
                            <SelectItem value="reel">Reel</SelectItem>
                            <SelectItem value="historia">Historia</SelectItem>
                          </SelectContent>
                        </Select>
                      : <div className="text-sm px-3 py-2 rounded-md bg-muted/40 border border-border min-h-[38px] capitalize">{modalPost.format}</div>}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Contenido / bajada</Label>
                  {editMode
                    ? <Input key={`p-${modalPost.id}`} defaultValue={modalPost.pista || ''} placeholder="Ej.: Mecánica · descripción corta"
                        onBlur={e => { if (e.target.value !== (modalPost.pista || '')) savePost(modalPost, { pista: e.target.value }); }} />
                    : <div className="text-sm px-3 py-2 rounded-md bg-muted/40 border border-border min-h-[38px]">{modalPost.pista || <span className="text-muted-foreground">—</span>}</div>}
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label>Etiquetas / hashtags</Label>
                    {editMode && <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => setSetsOpen(true)}><HashtagIcon className="h-3.5 w-3.5" /> Sets</Button>}
                  </div>
                  {(modalPost.tags || []).length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {(modalPost.tags || []).map(t => (
                        <span key={t} className={`inline-flex items-center gap-1 text-xs bg-muted rounded-full pl-2 py-0.5 ${editMode ? 'pr-1' : 'pr-2'}`}>
                          #{t}
                          {editMode && <Tip label="Quitar"><button onClick={() => removeTag(modalPost, t)} className="hover:text-red-600"><XMarkIcon className="h-3 w-3" /></button></Tip>}
                        </span>
                      ))}
                    </div>
                  ) : (!editMode && <span className="text-sm text-muted-foreground">Sin etiquetas</span>)}
                  {editMode && <Input value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="Escribí una etiqueta y Enter"
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(modalPost); } }} />}
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label>Texto del posteo (caption)</Label>
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" onClick={() => copyCaption(modalPost)}><ClipboardDocumentIcon className="h-3.5 w-3.5" /> Copiar</Button>
                  </div>
                  {editMode
                    ? <textarea rows={4} key={`cap-${modalPost.id}`} defaultValue={modalPost.caption || ''}
                        placeholder="Escribí el texto que va en Instagram. Al copiar se suman los hashtags abajo."
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                        onBlur={e => { if (e.target.value !== (modalPost.caption || '')) savePost(modalPost, { caption: e.target.value }); }} />
                    : <div className="text-sm px-3 py-2 rounded-md bg-muted/40 border border-border whitespace-pre-wrap min-h-[96px]">{modalPost.caption || <span className="text-muted-foreground">Sin texto</span>}</div>}
                </div>
                </div>{/* fin columna izquierda */}
                <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => togglePost(modalPost, 'realizado')}
                    className={`p-3 rounded-lg border text-left transition-colors ${modalPost.realizado ? 'border-foreground bg-foreground/5' : 'border-border hover:bg-muted'}`}>
                    <div className="flex items-center gap-2 text-sm font-medium"><CheckIcon className="h-4 w-4" /> Realizado</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Producido / editado</div>
                  </button>
                  <button onClick={() => togglePost(modalPost, 'subido')}
                    className={`p-3 rounded-lg border text-left transition-colors ${modalPost.subido ? 'border-blue-500 bg-blue-500/10' : 'border-border hover:bg-muted'}`}>
                    <div className="flex items-center gap-2 text-sm font-medium"><ArrowUpCircleIcon className="h-4 w-4" /> Subido</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Publicado en IG</div>
                  </button>
                </div>
                {/* Aprobación */}
                <div className="rounded-lg border border-border p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <ShieldCheckIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Aprobación</span>
                    {modalPost.reviewStatus === 'revision' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600 dark:text-amber-400 font-medium ml-auto">En revisión</span>}
                    {modalPost.reviewStatus === 'aprobado' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 font-medium ml-auto">Aprobado</span>}
                  </div>
                  {(!modalPost.reviewStatus || modalPost.reviewStatus === 'borrador') && (
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setReview(modalPost, 'revision')}><ClockIcon className="h-4 w-4" /> Marcar lista para revisar</Button>
                      {isAdmin && <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setReview(modalPost, 'aprobado')}><CheckIcon className="h-4 w-4" /> Aprobar</Button>}
                    </div>
                  )}
                  {modalPost.reviewStatus === 'revision' && (
                    <div className="flex flex-wrap gap-2">
                      {isAdmin
                        ? <>
                            <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setReview(modalPost, 'aprobado')}><CheckIcon className="h-4 w-4" /> Aprobar</Button>
                            <Button size="sm" variant="outline" onClick={() => setReview(modalPost, 'borrador')}>Volver a borrador</Button>
                          </>
                        : <><p className="text-xs text-muted-foreground">Esperando aprobación del admin.</p><Button size="sm" variant="ghost" onClick={() => setReview(modalPost, 'borrador')}>Cancelar revisión</Button></>}
                    </div>
                  )}
                  {modalPost.reviewStatus === 'aprobado' && isAdmin && (
                    <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => setReview(modalPost, 'revision')}>Quitar aprobación</Button>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>¿Qué se subió? · detalle real</Label>
                  {editMode
                    ? <textarea rows={3} defaultValue={modalPost.note || ''}
                        placeholder="Ej.: Service Toyota Hilux 2019 — entró 9:30, salió 12:10."
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                        onChange={(e) => saveNote(modalPost, e.target.value)} />
                    : <div className="text-sm px-3 py-2 rounded-md bg-muted/40 border border-border whitespace-pre-wrap min-h-[72px]">{modalPost.note || <span className="text-muted-foreground">—</span>}</div>}
                </div>
                <div className="space-y-1.5">
                  <Label>Link de la publicación (IG)</Label>
                  {editMode ? (
                    <div className="flex gap-2">
                      <Input key={`ig-${modalPost.id}`} type="url" defaultValue={modalPost.igLink || ''}
                        placeholder="https://www.instagram.com/p/..."
                        onBlur={e => { if (e.target.value !== (modalPost.igLink || '')) savePost(modalPost, { igLink: e.target.value.trim() }); }} />
                      {modalPost.igLink && (
                        <Tip label="Abrir publicación"><Button variant="outline" size="icon" className="shrink-0" onClick={() => window.open(modalPost.igLink, '_blank')}>
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </Button></Tip>
                      )}
                    </div>
                  ) : modalPost.igLink ? (
                    <a href={modalPost.igLink} target="_blank" rel="noreferrer" className="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all flex items-center gap-1.5">
                      <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0" /> {modalPost.igLink}
                    </a>
                  ) : (
                    <div className="text-sm px-3 py-2 rounded-md bg-muted/40 border border-border min-h-[38px]"><span className="text-muted-foreground">Sin link</span></div>
                  )}
                </div>
                {modalPost.dayNumber != null && (
                  <DayMedia
                    calendarId={active.id}
                    day={Number(modalPost.dayNumber)}
                    initialFolderId={dayFolders[Number(modalPost.dayNumber)]}
                    onFolder={(d, fid) => setDayFolders(prev => ({ ...prev, [d]: fid }))}
                    onFolderRemoved={(d) => setDayFolders(prev => { const n = { ...prev }; delete n[d]; return n; })}
                  />
                )}
                <div className="border-t border-border pt-3 flex justify-end">
                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-500/10 gap-1.5" onClick={() => deletePiece(modalPost)}>
                    <TrashIcon className="h-4 w-4" /> Eliminar pieza
                  </Button>
                </div>
                </div>{/* fin columna derecha */}
              </div>{/* fin grid 2 columnas */}
            </>
          )}
        </DialogContent>
      </Dialog>

      {showNew && <NewCalendarModal onClose={() => setShowNew(false)} onCreated={(id) => { setShowNew(false); fetchList().then(l => { const c = l.find(x => x.id === id); if (c) goToPeriod(c.month, c.year); }); }} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onCreated={(id) => { setShowImport(false); fetchList().then(l => { const c = l.find(x => x.id === id); if (c) goToPeriod(c.month, c.year); }); }} />}
    </div>
  );
};

// ── Estado vacío ──────────────────────────────────────────────────────────
const EmptyState: React.FC<{ onCreate: () => void; onSeed: () => void }> = ({ onCreate, onSeed }) => (
  <Card><CardContent className="py-16 text-center space-y-4">
    <CalendarDaysIcon className="h-12 w-12 text-muted-foreground mx-auto" />
    <div>
      <p className="font-medium">No hay calendarios creados</p>
      <p className="text-sm text-muted-foreground">Creá uno nuevo o cargá el ejemplo para empezar</p>
    </div>
    <div className="flex gap-2 justify-center">
      <Button variant="outline" onClick={onCreate} className="gap-1.5"><PlusIcon className="h-4 w-4" /> Crear calendario</Button>
      <Button onClick={onSeed} className="bg-black dark:bg-white text-white dark:text-black">Cargar ejemplo (Junio 2026)</Button>
    </div>
  </CardContent></Card>
);

// ── Material del día (carpeta de Drive enlazada) ──────────────────────────
interface DMedia { id: string; name: string; kind: 'image' | 'video'; }

const MiniThumb: React.FC<{ file: DMedia; onOpen: () => void; onDelete: () => void }> = ({ file, onOpen, onDelete }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let obj: string | null = null; let dead = false;
    axios.get(`/api/google-drive/files/${file.id}/thumbnail`, { responseType: 'blob' })
      .then(r => { if (dead) return; obj = URL.createObjectURL(r.data); setUrl(obj); }).catch(() => {});
    return () => { dead = true; if (obj) URL.revokeObjectURL(obj); };
  }, [file.id]);
  return (
    <div className="relative aspect-square rounded-md overflow-hidden bg-muted group">
      <Tip label={file.name}><button onClick={onOpen} className="w-full h-full">
        {url ? <img src={url} alt={file.name} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center">{file.kind === 'video' ? <PlayCircleIcon className="h-6 w-6 text-muted-foreground" /> : <PhotoIcon className="h-6 w-6 text-muted-foreground" />}</div>}
        {file.kind === 'video' && url && <PlayCircleIcon className="h-7 w-7 text-white/90 absolute inset-0 m-auto drop-shadow pointer-events-none" />}
      </button></Tip>
      <Tip label="Eliminar"><button onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-red-600 text-white rounded p-0.5">
        <TrashIcon className="h-3.5 w-3.5" />
      </button></Tip>
    </div>
  );
};

const DayMedia: React.FC<{ calendarId: string; day: number; initialFolderId?: string; onFolder: (day: number, id: string) => void; onFolderRemoved: (day: number) => void }> = ({ calendarId, day, initialFolderId, onFolder, onFolderRemoved }) => {
  const [folderId, setFolderId] = useState<string | null>(initialFolderId || null);
  const [files, setFiles] = useState<DMedia[]>([]);
  const [loading, setLoading] = useState(!!initialFolderId);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [viewer, setViewer] = useState<DMedia | null>(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [qrStatus, setQrStatus] = useState<'waiting' | 'received' | 'error'>('waiting');
  const [qrReceived, setQrReceived] = useState(0);
  const qrPollRef = useRef<number | null>(null);
  const qrBaselineRef = useRef<number | null>(null);

  const loadFiles = useCallback(async (fid: string) => {
    setLoading(true);
    try { const r = await axios.get('/api/google-drive/files', { params: { folderId: fid } }); setFiles(r.data.files || []); }
    catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { if (initialFolderId) { setFolderId(initialFolderId); loadFiles(initialFolderId); } }, [initialFolderId, loadFiles]);
  useEffect(() => () => { if (qrPollRef.current) window.clearInterval(qrPollRef.current); }, []);

  const openQr = async () => {
    try {
      const r = await axios.post(`/api/content-calendars/${calendarId}/days/${day}/photo-token`);
      const url = `${window.location.origin}/subir-material/${r.data.token}`;
      setQrUrl(await QRCode.toDataURL(url, { width: 240, margin: 2 }));
      setQrStatus('waiting'); setQrReceived(0); qrBaselineRef.current = null;
      setQrOpen(true);
      if (qrPollRef.current) window.clearInterval(qrPollRef.current);
      qrPollRef.current = window.setInterval(async () => {
        try {
          const pr = await axios.get(`/api/content-calendars/photo-upload/${r.data.token}/photos`);
          const list = Array.isArray(pr.data.files) ? pr.data.files : [];
          setFiles(list);
          if (pr.data.dayFolderId && !folderId) { setFolderId(pr.data.dayFolderId); onFolder(day, pr.data.dayFolderId); }
          // baseline = cantidad al abrir; si llegan nuevos → check verde
          if (qrBaselineRef.current === null) qrBaselineRef.current = list.length;
          else if (list.length > qrBaselineRef.current) { setQrReceived(list.length - qrBaselineRef.current); setQrStatus('received'); }
        } catch { setQrStatus('error'); }
      }, 3000);
    } catch (e: any) { toast.error(e.response?.data?.error || 'No se pudo generar el QR'); }
  };
  const closeQr = () => {
    setQrOpen(false);
    if (qrPollRef.current) { window.clearInterval(qrPollRef.current); qrPollRef.current = null; }
    if (folderId) loadFiles(folderId);
  };

  const ensureFolder = async (): Promise<string> => {
    if (folderId) return folderId;
    const r = await axios.post(`/api/content-calendars/${calendarId}/days/${day}/folder`);
    setFolderId(r.data.dayFolderId); onFolder(day, r.data.dayFolderId);
    return r.data.dayFolderId;
  };

  const onPick = async (fl: FileList) => {
    const arr = Array.from(fl).filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));
    if (!arr.length) { toast.error('Subí imágenes o videos'); return; }
    setUploading(true);
    try {
      const fid = await ensureFolder();
      const fd = new FormData(); arr.forEach(f => fd.append('files', f)); fd.append('folderId', fid);
      await axios.post('/api/google-drive/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Material subido'); loadFiles(fid);
    } catch (e: any) { toast.error(e.response?.data?.error || 'Error al subir (¿banco de medios configurado?)'); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  const openFile = async (f: DMedia) => {
    try { const r = await axios.get(`/api/google-drive/files/${f.id}/content`, { responseType: 'blob' }); window.open(URL.createObjectURL(r.data), '_blank'); }
    catch { toast.error('No se pudo abrir'); }
  };

  const removeFile = async (f: DMedia) => {
    if (!(await confirmDialog({ title: 'Eliminar material', description: `¿Eliminar "${f.name}"? Se manda a la papelera de Drive.`, confirmText: 'Eliminar' }))) return;
    try {
      await axios.delete(`/api/google-drive/files/${f.id}`);
      const remaining = files.filter(x => x.id !== f.id);
      setFiles(remaining);
      toast.success('Eliminado');
      // Si era el último archivo, borrar también la carpeta del día
      if (remaining.length === 0) {
        try {
          await axios.delete(`/api/content-calendars/${calendarId}/days/${day}/folder`);
          setFolderId(null); onFolderRemoved(day);
        } catch {}
      }
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <div className="space-y-2 border-t border-border pt-4">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5"><PaperClipIcon className="h-4 w-4" /> Material del día</Label>
        <input ref={inputRef} type="file" multiple accept="image/*,video/*" className="hidden" onChange={e => e.target.files && onPick(e.target.files)} />
        <div className="flex items-center gap-2">
          <Tip label="Escanear QR y subir desde el celular"><Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={openQr}>
            <QrCodeIcon className="h-3.5 w-3.5" /> QR celular
          </Button></Tip>
          <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => inputRef.current?.click()} disabled={uploading}>
            <ArrowUpTrayIcon className="h-3.5 w-3.5" /> {uploading ? 'Subiendo...' : 'Agregar'}
          </Button>
        </div>
      </div>

      <Dialog open={qrOpen} onOpenChange={(o) => { if (!o) closeQr(); }}>
        <DialogContent className="w-[95vw] max-w-xs text-center">
          <DialogHeader><DialogTitle>Subir material desde el celular</DialogTitle></DialogHeader>
          {qrStatus === 'received' ? (
            <div className="py-4 flex flex-col items-center gap-2">
              <CheckCircleIcon className="h-20 w-20 text-emerald-500" />
              <p className="text-base font-semibold text-emerald-600">¡Material recibido!</p>
              <p className="text-sm text-muted-foreground">{qrReceived} archivo{qrReceived !== 1 ? 's' : ''} subido{qrReceived !== 1 ? 's' : ''} desde el celular. Podés seguir subiendo o tocar Listo.</p>
            </div>
          ) : qrStatus === 'error' ? (
            <div className="py-4 flex flex-col items-center gap-2">
              <XCircleIcon className="h-20 w-20 text-red-500" />
              <p className="text-base font-semibold text-red-600">Error de conexión</p>
              <p className="text-sm text-muted-foreground">No pudimos verificar la subida. Revisá la conexión y reintentá.</p>
            </div>
          ) : (
            <>
              {qrUrl && <img src={qrUrl} alt="QR" className="mx-auto rounded-lg bg-white p-2" width={240} height={240} />}
              <p className="text-sm text-muted-foreground">Escaneá el QR con la cámara del celular y subí las fotos/videos. Aparecen acá automáticamente.</p>
            </>
          )}
          <DialogFooter>
            {qrStatus === 'error'
              ? <Button variant="outline" onClick={() => { setQrStatus('waiting'); }}>Reintentar</Button>
              : <Button variant="outline" onClick={closeQr}>Listo</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {loading ? (
        <p className="text-xs text-muted-foreground">Cargando...</p>
      ) : files.length === 0 ? (
        <p className="text-xs text-muted-foreground">Sin material todavía. La carpeta del día (dd/mm/aa) se crea sola al subir el primer archivo.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {files.map(f => <MiniThumb key={f.id} file={f} onOpen={() => setViewer(f)} onDelete={() => removeFile(f)} />)}
        </div>
      )}

      {/* Visor de material (foto/video) dentro de la app */}
      {viewer && (() => {
        const tk = localStorage.getItem('token') || '';
        const src = `/api/google-drive/files/${viewer.id}/content?token=${encodeURIComponent(tk)}`;
        return (
          <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4" onClick={() => setViewer(null)}>
            <div className="max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              {viewer.kind === 'video'
                ? <video src={src} controls autoPlay playsInline className="max-w-[90vw] max-h-[85vh] rounded-lg bg-black" />
                : <img src={src} alt={viewer.name} className="max-w-[90vw] max-h-[85vh] rounded-lg object-contain" />}
              <div className="mt-2 flex items-center justify-between gap-3 text-white text-sm">
                <span className="truncate">{viewer.name}</span>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => openFile(viewer)}>Abrir en pestaña</Button>
                  <Button variant="outline" size="sm" onClick={() => setViewer(null)}>Cerrar</Button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// ── Modal Nuevo ──────────────────────────────────────────────────────────
const NewCalendarModal: React.FC<{ onClose: () => void; onCreated: (id: string) => void }> = ({ onClose, onCreated }) => {
  const now = new Date();
  const [form, setForm] = useState({ name: '', month: now.getMonth() + 1, year: now.getFullYear(), account: '@rocam.taller', location: 'Oroño 620, San Lorenzo' });
  const [saving, setSaving] = useState(false);
  const create = async () => {
    if (!form.name.trim()) { toast.error('Poné un nombre'); return; }
    setSaving(true);
    try {
      const res = await axios.post('/api/content-calendars', { ...form, name: form.name.trim(), distribution: { real: 70, edu: 20, marca: 10 }, ephemerides: [], posts: [] });
      toast.success('Calendario creado'); onCreated(res.data.id);
    } catch (e: any) { toast.error(e.response?.data?.error || 'Error'); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-md">
        <DialogHeader><DialogTitle>Nuevo calendario</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Nombre *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Calendario de contenido" autoFocus /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Mes</Label>
              <Select value={String(form.month)} onValueChange={v => setForm(f => ({ ...f, month: Number(v) }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Año</Label><Input type="number" value={form.year} onChange={e => setForm(f => ({ ...f, year: Number(e.target.value) }))} /></div>
          </div>
          <div className="space-y-1.5"><Label>Cuenta</Label><Input value={form.account} onChange={e => setForm(f => ({ ...f, account: e.target.value }))} /></div>
          <div className="space-y-1.5"><Label>Ubicación</Label><Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={create} disabled={saving}>{saving ? 'Creando...' : 'Crear'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Modal Configurar fechas ───────────────────────────────────────────────
const CAT_LABELS: Record<string, string> = { comercial: '🛒 Comerciales', rubro: '🔧 Rubro automotor', tc: '🏁 Turismo Carretera', mundial: '⚽ Mundial' };
const DOW_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const DateConfigDialog: React.FC<{ open: boolean; onClose: () => void; onChanged: () => void }> = ({ open, onClose, onChanged }) => {
  const [cats, setCats] = useState<any[]>([]);
  const [dates, setDates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [nTitle, setNTitle] = useState('');
  const [nCat, setNCat] = useState('comercial');
  const [nMode, setNMode] = useState<'date' | 'fixed'>('date');
  const [nDate, setNDate] = useState('');
  const [nMonth, setNMonth] = useState('');
  const [nDay, setNDay] = useState('');
  const [nEmoji, setNEmoji] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get('/api/content-calendars/categories').then(r => r.data.categories || []).catch(() => []),
      axios.get('/api/content-calendars/special-dates/all').then(r => r.data.dates || []).catch(() => []),
    ]).then(([c, d]) => { setCats(c); setDates(d); }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const toggleCat = async (key: string, enabled: boolean) => {
    setCats(cs => cs.map(c => c.key === key ? { ...c, enabled } : c));
    try { await axios.patch(`/api/content-calendars/categories/${key}`, { enabled }); onChanged(); }
    catch { toast.error('No se pudo guardar'); }
  };
  const addDate = async () => {
    if (!nTitle.trim()) { toast.error('Poné un título'); return; }
    const body: any = { title: nTitle.trim(), category: nCat, emoji: nEmoji.trim() || null };
    if (nMode === 'date') { if (!nDate) { toast.error('Elegí la fecha'); return; } body.recurrence = 'date'; body.date = nDate; }
    else { const m = parseInt(nMonth, 10), d = parseInt(nDay, 10); if (!m || !d) { toast.error('Mes y día'); return; } body.recurrence = 'fixed'; body.month = m; body.day = d; }
    try { await axios.post('/api/content-calendars/special-dates', body); setNTitle(''); setNDate(''); setNMonth(''); setNDay(''); setNEmoji(''); load(); onChanged(); toast.success('Fecha agregada'); }
    catch { toast.error('No se pudo agregar'); }
  };
  const delDate = async (id: string) => {
    if (!(await confirmDialog({ title: 'Eliminar fecha', description: 'Se quita del calendario.', confirmText: 'Eliminar' }))) return;
    try { await axios.delete(`/api/content-calendars/special-dates/${id}`); load(); onChanged(); }
    catch { toast.error('No se pudo eliminar'); }
  };
  const fmtRule = (d: any) => d.recurrence === 'date' ? d.date
    : d.recurrence === 'fixed' ? `${String(d.day).padStart(2, '0')}/${String(d.month).padStart(2, '0')} · todos los años`
    : d.recurrence === 'nth_weekday' ? `${d.nth}º ${DOW_ES[d.weekday]} de ${MONTHS[d.month - 1]}` : '';

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Configurar fechas del calendario</DialogTitle></DialogHeader>

        <div>
          <p className="text-sm font-medium mb-2">Categorías que se muestran</p>
          <div className="flex flex-wrap gap-2">
            {cats.map(c => (
              <label key={c.key} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors ${c.enabled ? 'bg-primary/10 border-primary/40' : 'border-border hover:bg-muted/50'}`}>
                <input type="checkbox" checked={!!c.enabled} onChange={e => toggleCat(c.key, e.target.checked)} className="accent-primary" />
                <span>{c.emoji} {c.label}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">Los feriados/patrias salen de Google (ya sin fechas islámicas ni judías). F1/MotoGP se gestionan desde Redes → Eventos (Google).</p>
        </div>

        <div className="border-t border-border pt-3">
          <p className="text-sm font-medium mb-2">Agregar fecha</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input placeholder="Título (ej: TC en Buenos Aires)" value={nTitle} onChange={e => setNTitle(e.target.value)} />
            <Input placeholder="Emoji (opcional)" value={nEmoji} onChange={e => setNEmoji(e.target.value)} />
            <Select value={nCat} onValueChange={setNCat}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.keys(CAT_LABELS).map(k => <SelectItem key={k} value={k}>{CAT_LABELS[k]}</SelectItem>)}</SelectContent></Select>
            <Select value={nMode} onValueChange={(v: any) => setNMode(v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="date">Fecha exacta</SelectItem><SelectItem value="fixed">Todos los años (día/mes)</SelectItem></SelectContent></Select>
            {nMode === 'date'
              ? <DatePicker value={nDate} onChange={setNDate} />
              : <div className="flex gap-2"><Input type="number" placeholder="Día" min={1} max={31} value={nDay} onChange={e => setNDay(e.target.value)} /><Input type="number" placeholder="Mes" min={1} max={12} value={nMonth} onChange={e => setNMonth(e.target.value)} /></div>}
            <Button onClick={addDate} className="gap-1"><PlusIcon className="h-4 w-4" />Agregar</Button>
          </div>
        </div>

        <div className="border-t border-border pt-3">
          <p className="text-sm font-medium mb-2">Fechas cargadas</p>
          {loading ? <p className="text-sm text-muted-foreground">Cargando…</p> : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {dates.map(d => (
                <div key={d.id} className="flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-muted/40">
                  <span className="shrink-0">{d.emoji}</span>
                  <span className="flex-1 truncate">{d.title}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{(CAT_LABELS[d.category] || '').split(' ')[0]} · {fmtRule(d)}</span>
                  <Tip label="Eliminar"><Button variant="ghost" size="icon" className="size-7 text-muted-foreground hover:text-red-500" onClick={() => delDate(d.id)}><TrashIcon className="h-3.5 w-3.5" /></Button></Tip>
                </div>
              ))}
              {!dates.length && <p className="text-sm text-muted-foreground">Sin fechas cargadas.</p>}
            </div>
          )}
        </div>

        <DialogFooter><Button variant="outline" onClick={onClose}>Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Modal Importar ────────────────────────────────────────────────────────
const ImportModal: React.FC<{ onClose: () => void; onCreated: (id: string) => void }> = ({ onClose, onCreated }) => {
  const now = new Date();
  const [meta, setMeta] = useState({ name: '', month: now.getMonth() + 1, year: now.getFullYear() });
  const [posts, setPosts] = useState<any[]>([]);
  const [fileName, setFileName] = useState('');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const parseCSV = (text: string) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const idx = (n: string) => headers.indexOf(n);
    return lines.slice(1).map(line => {
      const cols = line.split(',').map(c => c.trim());
      return {
        num: cols[idx('num')] || cols[idx('numero')] || '',
        category: (cols[idx('categoria')] || cols[idx('category')] || 'real').toLowerCase(),
        format: (cols[idx('formato')] || cols[idx('format')] || 'post').toLowerCase(),
        pista: cols[idx('pista')] || '',
        theme: cols[idx('tema')] || cols[idx('theme')] || '',
        dayNumber: Number(cols[idx('dia')] || cols[idx('day')] || cols[idx('daynumber')]) || null,
      };
    }).filter(p => p.num || p.theme);
  };
  const onFile = async (file: File) => {
    setFileName(file.name);
    const text = await file.text();
    try {
      if (file.name.endsWith('.json')) {
        const data = JSON.parse(text);
        const arr = Array.isArray(data) ? data : (data.posts || []);
        setPosts(arr);
        if (data.name && !meta.name) setMeta(m => ({ ...m, name: data.name, month: data.month || m.month, year: data.year || m.year }));
      } else setPosts(parseCSV(text));
      toast.success('Archivo leído');
    } catch { toast.error('No se pudo leer el archivo'); }
  };
  const importNow = async () => {
    if (!meta.name.trim()) { toast.error('Poné un nombre'); return; }
    if (posts.length === 0) { toast.error('No hay piezas'); return; }
    setSaving(true);
    try {
      const res = await axios.post('/api/content-calendars/import', { ...meta, name: meta.name.trim(), distribution: { real: 70, edu: 20, marca: 10 }, ephemerides: [], posts });
      toast.success(`Importado (${posts.length} piezas)`); onCreated(res.data.id);
    } catch (e: any) { toast.error(e.response?.data?.error || 'Error'); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-md">
        <DialogHeader><DialogTitle>Importar calendario</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">Subí un <b>CSV</b> o <b>JSON</b>. Columnas CSV: <code className="bg-muted px-1 rounded">num, categoria, formato, pista, tema, dia</code></p>
          <div className="space-y-1.5"><Label>Nombre *</Label><Input value={meta.name} onChange={e => setMeta(m => ({ ...m, name: e.target.value }))} placeholder="Calendario Julio" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Mes</Label>
              <Select value={String(meta.month)} onValueChange={v => setMeta(m => ({ ...m, month: Number(v) }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Año</Label><Input type="number" value={meta.year} onChange={e => setMeta(m => ({ ...m, year: Number(e.target.value) }))} /></div>
          </div>
          <input ref={fileRef} type="file" accept=".csv,.json" className="hidden" onChange={e => e.target.files?.[0] && onFile(e.target.files[0])} />
          <button onClick={() => fileRef.current?.click()} className="w-full py-4 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:bg-muted transition-colors">
            {fileName ? `📄 ${fileName} — ${posts.length} piezas` : 'Click para seleccionar CSV o JSON'}
          </button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={importNow} disabled={saving || posts.length === 0}>{saving ? 'Importando...' : `Importar ${posts.length || ''}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Seed Junio 2026 ───────────────────────────────────────────────────────
async function seedJune(refresh: () => Promise<any>, setActiveId: (id: string) => void) {
  const posts = [
    { num: '01', category: 'real', format: 'post', pista: 'Mecánica', theme: 'Service completo terminado, con tiempos de entrada y salida', dayNumber: 8 },
    { num: '02', category: 'real', format: 'reel', pista: 'Gomería', theme: 'Colocación de neumáticos + balanceo en timelapse', dayNumber: 9 },
    { num: '03', category: 'real', format: 'carrusel', pista: 'Lavadero', theme: 'Detallado antes / después', dayNumber: 10 },
    { num: '15', category: 'edu', format: 'reel', pista: 'Educación', theme: 'Banda de rodamiento bajo 1,6 mm: cuándo cambiar', dayNumber: 11 },
    { num: '04', category: 'real', format: 'post', pista: 'Lubricentro', theme: 'Cambio de aceite sintético, sin turno', dayNumber: 12 },
    { num: '05', category: 'real', format: 'carrusel', pista: 'Mecánica', theme: 'Cambio de frenos, pieza gastada vs nueva', dayNumber: 14 },
    { num: '06', category: 'real', format: 'post', pista: 'Gomería', theme: 'Reparación de pinchadura con parche interno', dayNumber: 15 },
    { num: '07', category: 'real', format: 'reel', pista: 'Lavadero', theme: 'Lavado de motor, proceso', dayNumber: 16 },
    { num: '08', category: 'real', format: 'post', pista: 'Mecánica', theme: 'Distribución completa: correa, tensor y bomba', dayNumber: 17 },
    { num: '17', category: 'edu', format: 'reel', pista: 'Educación', theme: 'Qué hacer con la luz amarilla del tablero', dayNumber: 18 },
    { num: '09', category: 'real', format: 'post', pista: 'Lubricentro', theme: 'Service de caja automática con aceite según fábrica', dayNumber: 19 },
    { num: '19', category: 'marca', format: 'post', pista: 'Marca', theme: 'Cuatro pistas, un solo taller — Oroño 620, San Lorenzo', dayNumber: 21 },
    { num: '12', category: 'real', format: 'carrusel', pista: 'Lavadero', theme: 'Pulido y encerado antes / después', dayNumber: 22 },
    { num: '10', category: 'real', format: 'reel', pista: 'Gomería', theme: 'Rotación de neumáticos en 10 minutos', dayNumber: 23 },
    { num: '13', category: 'real', format: 'post', pista: 'Lubricentro', theme: 'Cambio de filtro de aire y de habitáculo', dayNumber: 24 },
    { num: '11', category: 'real', format: 'reel', pista: 'Mecánica', theme: 'Diagnóstico con scanner de luz de tablero', dayNumber: 25 },
    { num: '16', category: 'edu', format: 'carrusel', pista: 'Educación', theme: 'Cada cuánto cambiar el aceite', dayNumber: 26 },
    { num: '20', category: 'marca', format: 'historia', pista: 'Marca', theme: '4.9 en Google · 12.000+ vehículos desde 2019', dayNumber: 27, isStoryFeatured: true },
    { num: '14', category: 'real', format: 'carrusel', pista: 'Mecánica', theme: 'Trabajo de varios días, paso a paso', dayNumber: 28 },
    { num: '18', category: 'edu', format: 'post', pista: 'Educación', theme: 'Por qué lavar seguido en verano', dayNumber: 29 },
  ];
  const ephemerides = [
    { type: 'patria', dateLabel: 'LUN 15', name: '<b>Feriado por Güemes</b> · trasladado del 17 · finde largo 13–15', day: 15, kind: 'holiday' },
    { type: 'patria', dateLabel: 'MIÉ 17', name: '<b>Día de Güemes</b> · fecha real · homenaje', day: 17, kind: 'flag' },
    { type: 'patria', dateLabel: 'SÁB 20', name: '<b>Día de la Bandera</b> · feriado inamovible', day: 20, kind: 'holiday' },
    { type: 'rubro', dateLabel: '12–13', name: '<b>Finde largo</b> · check pre-viaje antes de salir', day: 12, kind: 'key' },
    { type: 'rubro', dateLabel: 'DOM 21', name: '<b>Día del Padre</b> · regalá un service', day: 21, kind: 'key' },
    { type: 'rubro', dateLabel: 'DOM 21', name: '<b>Inicio del invierno</b> · prepará el auto', day: 21, kind: 'key' },
    { type: 'carrera', dateLabel: 'DOM 14', name: '<b>F1</b> · GP de España (Barcelona)', day: 14, kind: 'race' },
    { type: 'carrera', dateLabel: '20–21', name: '<b>TC</b> · Rafaela (Santa Fe)', day: 20, kind: 'race' },
    { type: 'carrera', dateLabel: 'DOM 28', name: '<b>F1</b> · GP de Austria', day: 28, kind: 'race' },
  ];
  try {
    const res = await axios.post('/api/content-calendars', { name: 'Calendario de contenido', month: 6, year: 2026, account: '@rocam.taller', location: 'Oroño 620, San Lorenzo', distribution: { real: 70, edu: 20, marca: 10 }, ephemerides, posts });
    toast.success('Calendario de Junio 2026 cargado');
    await refresh(); setActiveId(res.data.id);
  } catch { toast.error('Error al crear el calendario'); }
}

export default ContentCalendarPage;
